<template>
  <div class="adjustments-container">
    <div class="adjust-button-container">
      <button class="add-adjustment" @click="openAddModal">{{ isAdmin ? "Add Adjustment" : "Add Expense" }}</button>
    </div>
    <AdjustmentTable 
      :loading="loading" 
      :data="filteredAdjustments"
      :employee-list="employeeList"
      :working-days="workingDays"
      :working-hours="workingHours"
      :working-days-in-month="workingDaysInMonth"
      :can-modify="canModify"
      @edit="openEditModal"
      @openDeleteModal="openDeleteModal"
    />
    
    <ReuseableModal 
      :modelValue="isModalOpen"  
      @update:modelValue="isModalOpen = $event"
      :title="modalTitle" 
      :isLarge="true"
      submitButtonText="Save" 
      @submit="saveAdjustment"
      :loading="isLoading"
    >
      <form @submit.prevent="saveAdjustment()" style="padding: 10px 0px;">
        <div class="form-group row">
          <div v-if="isAdmin" class="col-md-6" style="margin-bottom: 7px;">
            <CustomDropdown
              id="employee"
              label="Employee"
              :options="employeeOptions"
              :required="true"
              :value="selectedEmployeeId"
              placeholder="Select Employee"
              :show-error="employeeError !== ''"
              :error-message="employeeError"
              @change="selectEmployeeId"
            />
          </div>
          <div v-if="isAdmin" class="col-md-6" style="margin-bottom: 7px;">
            <CustomDropdown
              id="adjustmentType"
              label="Adjustment Type"
              :options="adjustmentTypeOptions"
              :required="true"
              :value="selectedAdjustmentType"
              placeholder="Select Type"
              :show-error="typeError !== ''"
              :error-message="typeError"
              @change="selectAdjustmentType"
            />
          </div>
        </div>
        <div class="form-group row">
          <div v-if="isExpense" class="col-md-6" style="margin-bottom: 7px;">
            <label for="expenseAmount">Amount (Rs.) <span style="color: red; font-size: 14px;">*</span></label>
            <input
              id="expenseAmount"
              type="number"
              v-model.number="expenseAmount"
              placeholder="Enter amount"
              class="form-control"
              min="1"
              step="any"
            />
            <span v-if="amountError" class="text-danger">{{ amountError }}</span>
          </div>
          <div v-else class="col-md-6" style="margin-bottom: 7px;">
            <CustomDropdown
              id="amount"
              label="Amount"
              :options="amountOptions"
              :required="true"
              :value="amount"
              placeholder="Select Amount"
              :show-error="amountError !== ''"
              :error-message="amountError"
              @change="handleAmountChange"
              :custom="true"
            />
            <input
              v-if="amount === 'Custom Value'"
              type="number"
              v-model.number="customAmount"
              placeholder="Enter custom amount"
              class="form-control"
              min="0"
              @input="validateForm"
              style="margin-top: 5px; width: 100%;"
            />
          </div>
          <div class="col-md-6" style="margin-bottom: 7px;">
            <CustomDropdown
              id="month"
              label="Month"
              :options="monthItems"
              :required="true"
              :value="selectedModalMonth"
              placeholder="Select Month"
              :show-error="monthError !== ''"
              :error-message="monthError"
              @change="selectModalMonth"
            />
            <span v-if="monthError" class="text-danger">{{ monthError }}</span>
          </div>
        </div>
        <div class="form-group row">
          <div class="col-md-6" style="margin-bottom: 7px;">
            <CustomDropdown
              id="year"
              label="Year"
              :options="yearItems"
              :required="true"
              :value="selectedModalYear"
              placeholder="Select Year"
              :show-error="yearError !== ''"
              :error-message="yearError"
              @change="selectModalYear"
            />
          </div>
          <div class="col-md-6">
            <label for="reason">{{ isExpense ? "Description" : "Reason" }} <span style="color: red; font-size: 14px;">*</span></label>
            <textarea 
              id="reason" 
              class="form-control" 
              v-model="reason" 
              :placeholder="isExpense ? 'What was this expense for?' : 'Enter reason'" 
              @input="checkReasonLength"
            ></textarea>
            <span v-if="reasonError" class="text-danger">{{ reasonError }}</span>
            <span style="display: flex; justify-content: end; font-size: 12px; color: #666;">
              Characters: {{ reasonLength }}/300
            </span>
          </div>
        </div>
        <div v-if="isExpense" class="form-group row">
          <div class="col-md-6">
            <label for="receipt">Receipt <span class="optional-label">(optional)</span></label>
            <input
              id="receipt"
              ref="receiptInput"
              type="file"
              accept="image/*,.pdf"
              class="form-control"
              @change="onReceiptSelected"
            />
            <div v-if="receiptFile || receiptUrl" class="receipt-current">
              <a v-if="!receiptFile" :href="receiptUrl" target="_blank" rel="noopener">View current receipt</a>
              <span v-else>{{ receiptFile.name }}</span>
              <button type="button" class="receipt-remove" @click="removeReceipt">Remove</button>
            </div>
            <span v-if="receiptError" class="text-danger">{{ receiptError }}</span>
          </div>
        </div>
      </form>
    </ReuseableModal>

    <!-- Delete Confirmation Modal -->
    <ReuseableModal 
      :modelValue="isDeleteModalOpen"  
      @update:modelValue="isDeleteModalOpen = $event"
      title="Confirm Delete" 
      :isLarge="false"
      submitButtonText="Delete" 
      cancelButtonText="Cancel"
      @submit="confirmDelete"
      :loading="isLoading"
    >
      <p v-if="deleteItem && deleteItem.employeeName">
        Are you sure you want to delete the adjustment for <strong>{{ deleteItem.employeeName }}</strong>?
      </p>
      <p v-else>Are you sure you want to delete this adjustment?</p>
    </ReuseableModal>
  </div>
</template>

<script>
import CustomDropdown from "src/components/UIComponents/CustomDropdown.vue";
import ReuseableModal from "src/components/UIComponents/ReuseableModal.vue";
import AdjustmentTable from "./AdjustmentTable.vue";
import firebaseDB from "../../../assets/config/firebase";
import { monthNames } from "../../../assets/config/constants";
import { generateYearsArray, calculateTotalWorkingDays } from "../../../assets/config/helpers";
import { settings } from "../../../utils/genralSpecificSettings";
import { calculatePerDaySalary, calculatePerHourSalary } from "../../../utils/perdaySallaryUtils";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import swal from "sweetalert";
import { handleActivityNotification } from "../../../services/notificationService";

const MAX_RECEIPT_BYTES = 5 * 1024 * 1024;
export default {
  name: "Adjustments",
  components: {
    CustomDropdown,
    ReuseableModal,
    AdjustmentTable
  },
  props: {
    selectedMonth: String,
    selectedYear: Number,
    employeeList: Array,
    isAdmin: Boolean,
    selectedEmployee: Object,
    constants: Object
  },
  data() {
    return {
      loading: false,
      isModalOpen: false,
      isDeleteModalOpen: false,
      isLoading: false,
      isEditMode: false,
      currentAdjustment: null,
      deleteItem: null,
      selectedAdjustmentType: "",
      amount: null,
      customAmount: null,
      reason: "",
      selectedModalMonth: this.selectedMonth || "",
      selectedModalYear: String(this.selectedYear) || "",
      selectedEmployeeId: this.selectedEmployee ? this.selectedEmployee.username : "",
      typeError: "",
      amountError: "",
      monthError: "",
      yearError: "",
      employeeError: "",
      reasonError: "",
      adjustments: [],
      expenseAmount: null,
      receiptFile: null,
      receiptUrl: "",
      receiptError: "",
      processedMonths: {}, // "<year>_<month>" -> true when the current user's payroll is processed
      workingDays: [],
      workingHours: 9,
      workingDaysInMonth: 1
    };
  },
  computed: {
    adjustmentTypeOptions() {
      return this.isAdmin ? ["Fine", "Bonus", "Advance Loan", "Expense"] : ["Expense"];
    },
    currentUser() {
      return this.$store.state.auth.user || {};
    },
    isExpense() {
      return this.selectedAdjustmentType === "Expense";
    },
    modalTitle() {
      if (!this.isAdmin) return this.isEditMode ? "Edit Expense" : "Add Expense";
      return "Add/Edit Adjustment";
    },
    monthItems() {
      return monthNames;
    },
    yearItems() {
      return generateYearsArray().map(year => String(year));
    },
    employeeOptions() {
      return this.employeeList.map(user => user.username);
    },
    filteredAdjustments() {
      return this.adjustments.filter(adj => {
        const monthMatch = !this.selectedMonth || adj.month === this.selectedMonth;
        const yearMatch = !this.selectedYear || Number(adj.year) === Number(this.selectedYear);
        const employeeMatch = !this.selectedEmployee || adj.employeeId === this.selectedEmployee.id;
        return monthMatch && yearMatch && employeeMatch;
      });
    },
    amountOptions() {
      const employee = this.employeeList.find(emp => emp.username === this.selectedEmployeeId);
      if (!employee || !employee.basicSalary || !this.selectedModalMonth || !this.selectedModalYear) {
        return ["Custom Value", "1x", "2x", "3x", "4x", "5x", "1day", "2days"];
      }

      const empWorkingDays = this.workingDays.length > 0 
        ? this.workingDays 
        : (employee.workingDays || this.constants.days || ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]);
      
      const empTotalHours = this.workingHours 
        || Number(employee.productiveHours) 
        || Number(this.constants.totalHours) 
        || 9;

      const selectedMonthNum = monthNames.indexOf(this.selectedModalMonth) + 1;
      const selectedYearNum = parseInt(this.selectedModalYear);
      const requiredWorkingDaysInMonth = calculateTotalWorkingDays(selectedYearNum, selectedMonthNum, empWorkingDays);

      if (requiredWorkingDaysInMonth === 0) {
        return ["Custom Value", "1x", "2x", "3x", "4x", "5x", "1day", "2days"];
      }

      const perDaySalary = calculatePerDaySalary(employee.basicSalary, employee.extraAllowance || 0, requiredWorkingDaysInMonth);
      const perHourSalary = calculatePerHourSalary(perDaySalary, empTotalHours);

      const calc = (n, isDay) => (isDay ? perDaySalary * n : perHourSalary * n).toFixed(2);

      const options = [
        `${calc(1, false)} (1x)`,
        `${calc(2, false)} (2x)`,
        `${calc(3, false)} (3x)`,
        `${calc(4, false)} (4x)`,
        `${calc(5, false)} (5x)`,
        `${calc(1, true)} (1 day)`,
        `${calc(2, true)} (2 days)`,
        "Custom Value"
      ];

      if (this.isEditMode && this.currentAdjustment && /^\d+$/.test(String(this.currentAdjustment.amount))) {
        options.unshift(String(this.currentAdjustment.amount));
      }

      return options;
    },
    reasonLength() {
      return this.reason.length;
    }
  },
  watch: {
    selectedMonth(newMonth) {
      this.selectedModalMonth = newMonth || "";
      this.fetchAdjustments();
      this.getSettingData();
    },
    selectedYear(newYear) {
      this.selectedModalYear = String(newYear) || "";
      this.fetchAdjustments();
      this.getSettingData();
    },
    amount(newAmount) {
      if (newAmount !== "Custom Value") this.customAmount = null;
    }
  },
  async created() {
    await this.fetchAdjustments();
    await this.getSettingData();
  },
  methods: {
    async getSettingData() {
      try {
        const userId = this.selectedEmployee ? this.selectedEmployee.id : null;
        const response = await settings(userId, this.constants || { 
          days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], 
          totalHours: 9 
        });
        if (response) {
          this.workingDays = Array.isArray(response.days) ? response.days : (response.days ? [response.days] : []);
          this.workingHours = response.totalHours || 9;
        } else {
          this.workingDays = this.constants.days || [];
          this.workingHours = this.constants.totalHours || 9;
        }
        // Calculate working days for the selected month
        const selectedMonthNum = monthNames.indexOf(this.selectedModalMonth) + 1;
        const selectedYearNum = parseInt(this.selectedModalYear);
        this.workingDaysInMonth = calculateTotalWorkingDays(selectedYearNum, selectedMonthNum, this.workingDays);
      } catch (error) {
        console.error("Error fetching settings:", error);
        this.workingDays = this.constants.days || [];
        this.workingHours = this.constants.totalHours || 9;
        this.workingDaysInMonth = 1;
      }
    },
    async fetchAdjustments() {
      this.loading = true;
      try {
        if (this.isAdmin) {
          const userId = this.selectedEmployee ? this.selectedEmployee.id : null;
          this.adjustments = await firebaseDB.getAdjustments(this.selectedYear, this.selectedMonth, userId);
        } else {
          // Employees only see their own expenses
          const own = await firebaseDB.getAdjustments(this.selectedYear, this.selectedMonth, this.currentUser.uid);
          this.adjustments = own.filter(adj => adj.adjustmentType === "Expense");
          await this.refreshProcessedMonths();
        }
      } catch (error) {
        swal("Error", "Failed to load adjustments.","error");
        console.error("Error:", error);
        this.adjustments = [];
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.resetForm();
      this.isEditMode = false;
      if (!this.isAdmin) {
        this.selectedAdjustmentType = "Expense";
        this.selectedEmployeeId = this.currentUser.username || "";
      }
      this.isModalOpen = true;
    },
    async refreshProcessedMonths() {
      const periods = new Set(this.adjustments.map(adj => `${adj.year}_${adj.month}`));
      if (this.selectedMonth && this.selectedYear) periods.add(`${this.selectedYear}_${this.selectedMonth}`);
      const processed = {};
      await Promise.all([...periods].map(async key => {
        const [year, month] = key.split("_");
        processed[key] = await firebaseDB.isPayrollProcessed(year, month, this.currentUser.uid);
      }));
      this.processedMonths = processed;
    },
    // Never throws: a failed notification shouldn't fail the save
    async notifyAdminsOfExpense(action, adjustment) {
      try {
        await handleActivityNotification(
          {
            type: "expense",
            action,
            amount: adjustment.amount,
            month: adjustment.month,
            year: adjustment.year,
            description: adjustment.reason,
            activityOnUser: this.currentUser.uid,
            activityOnUserName: this.currentUser.username,
            activityByUserName: this.currentUser.username,
          },
          this.$store.state.auth,
          this.currentUser.uid
        );
      } catch (error) {
        console.warn("Failed to notify admins about expense:", error);
      }
    },
    // Admins can change any adjustment; employees only their own expenses in a month that isn't processed yet
    canModify(item) {
      if (this.isAdmin) return true;
      return item.adjustmentType === "Expense"
        && item.employeeId === this.currentUser.uid
        && !this.processedMonths[`${item.year}_${item.month}`];
    },
    onReceiptSelected(event) {
      const file = event.target.files && event.target.files[0];
      this.receiptError = "";
      if (!file) return;
      const isAllowed = file.type.startsWith("image/") || file.type === "application/pdf";
      if (!isAllowed) {
        this.receiptError = "Receipt must be an image or PDF";
      } else if (file.size > MAX_RECEIPT_BYTES) {
        this.receiptError = "Receipt must be 5 MB or smaller";
      } else {
        this.receiptFile = file;
        return;
      }
      event.target.value = "";
    },
    removeReceipt() {
      this.receiptFile = null;
      this.receiptUrl = "";
      this.receiptError = "";
      if (this.$refs.receiptInput) this.$refs.receiptInput.value = "";
    },
    async openEditModal(item) {
      this.currentAdjustment = item;
      this.selectedAdjustmentType = item.adjustmentType;
      this.reason = item.reason;
      this.selectedModalMonth = item.month;
      this.selectedModalYear = String(item.year);
      const employee = this.employeeList.find(emp => emp.uid === item.employeeId) || {};
      this.selectedEmployeeId = employee.username || "";
      await this.getSettingData();

      this.expenseAmount = item.adjustmentType === "Expense" ? Number(item.amount) || null : null;
      this.receiptUrl = item.receiptUrl || "";
      this.receiptFile = null;
      this.receiptError = "";
      const stored = String(item.amount).trim();
      let dropdownValue = null;

      if (/^\d+$/.test(stored)) {
        dropdownValue = stored;
        this.customAmount = Number(stored);
      } else if (/^\d+x$/.test(stored)) {
        dropdownValue = this.amountOptions.find(o => o.includes(`(${stored})`));
      } else if (/^\d+days?$/.test(stored)) {
        const num = stored.match(/^\d+/)[0];
        const label = num === "1" ? "1 day" : `${num} days`;
        dropdownValue = this.amountOptions.find(o => o.includes(`(${label})`));
      }

      this.amount = dropdownValue || "Custom Value";
      this.isEditMode = true;
      this.isModalOpen = true;
    },
    handleAmountChange(newAmount) {
      this.amount = newAmount;
      if (newAmount === "Custom Value") {
        this.customAmount = this.isEditMode && /^\d+$/.test(String(this.currentAdjustment.amount))
          ? Number(this.currentAdjustment.amount)
          : null;
      } else {
        this.customAmount = null;
      }
    },
    openDeleteModal(item) {
      this.deleteItem = item;
      this.isDeleteModalOpen = true;
    },

    selectAdjustmentType(type) { this.selectedAdjustmentType = type; },
    selectModalMonth(month) { this.selectedModalMonth = month; },
    selectModalYear(year) { this.selectedModalYear = year; },
    selectEmployeeId(name) {
      this.selectedEmployeeId = name;
      this.amount = null;
      this.customAmount = null;
    },
    validateForm() {
      this.employeeError = this.isAdmin && !this.selectedEmployeeId ? "Employee is required" : "";
      this.typeError = !this.selectedAdjustmentType ? "Type is required" : "";
      this.monthError = !this.selectedModalMonth ? "Month is required" : "";
      this.yearError = !this.selectedModalYear ? "Year is required" : "";
      this.reasonError = !this.reason ? "Reason is required" : this.reason.length > 300 ? "Max 300 characters" : "";
      if (this.isExpense) {
        this.amountError = !(Number(this.expenseAmount) > 0) ? "Enter an amount greater than 0" : "";
      } else {
        this.amountError = !this.amount ? "Amount is required" : this.amount === "Custom Value" && !this.customAmount ? "Enter custom amount" : "";
      }

      return !this.employeeError && !this.typeError && !this.monthError && !this.yearError && !this.reasonError && !this.amountError && !this.receiptError;
    },

    /*** SAVE WITH TOAST MESSAGES ***/
    async saveAdjustment() {
      if (!this.validateForm()) return;
      this.isLoading = true;
      try {
        const employee = this.isAdmin
          ? this.employeeList.find(emp => emp.username === this.selectedEmployeeId) || {}
          : { uid: this.currentUser.uid, username: this.currentUser.username };
        const year = Number(this.selectedModalYear);

        if (!this.isAdmin && await firebaseDB.isPayrollProcessed(year, this.selectedModalMonth, employee.uid)) {
          swal("Payroll already processed", `Your payroll for ${this.selectedModalMonth} ${year} is already processed. Choose another month.`, "warning");
          return;
        }

        let storedAmount;
        if (this.isExpense) {
          // Stored as a plain number string, which payroll treats as a fixed amount
          storedAmount = String(Number(this.expenseAmount));
        } else if (this.amount === "Custom Value") {
          storedAmount = this.customAmount;
        } else {
          const match = this.amount.match(/\((\d+(?:x| ?days?))\)/i);
          if (match) {
            const raw = match[1]; // "1 day" → "1day"
            storedAmount = raw
            if (storedAmount.includes('day') && !storedAmount.endsWith('s') && storedAmount !== '1 day') {
              storedAmount += 's';
            }
          } else {
            storedAmount = /^\d+$/.test(this.amount) ? this.amount : "1x";
          }
        }

        const adjustmentData = {
          employeeId: employee.uid || "",
          employeeName: employee.username || "Unknown",
          adjustmentType: this.selectedAdjustmentType,
          amount: storedAmount,
          reason: this.reason,
          month: this.selectedModalMonth,
          year,
          impact: { Fine: "Deduction", Bonus: "Addition", "Advance Loan": "Deduction", Expense: "Addition" }[this.selectedAdjustmentType] || "Deduction",
          createdAt: Date.now(),
          addedBy: this.currentUser.username || "Admin"
        };
        if (this.isExpense) {
          adjustmentData.receiptUrl = this.receiptFile
            ? await uploadToCloudinary(this.receiptFile, "expense_receipts", "salary_slip")
            : this.receiptUrl || "";
        }
        if (this.isEditMode && this.currentAdjustment) {
          adjustmentData.id = this.currentAdjustment.id;
        }
        await firebaseDB.postAdjustment(adjustmentData);
        if (!this.isAdmin) await this.notifyAdminsOfExpense(this.isEditMode ? "updated" : "added", adjustmentData);
        await this.fetchAdjustments();

      
          this.isEditMode ?swal("Success","adjustment updated successfully.","success")  :swal("Success","Adjustment added successfully.","success")
 
        this.isModalOpen = false;
      } catch (error) {
        console.error("Save failed:", error);
        swal("Error","Failed to save adjustment. Please try again.","error");
      } finally {
        this.isLoading = false;
      }
    },
    async confirmDelete() {
      if (!this.deleteItem) return;
      this.isLoading = true;
      try {
        await firebaseDB.deleteAdjustment(this.deleteItem.id);
        if (!this.isAdmin) await this.notifyAdminsOfExpense("deleted", this.deleteItem);
        await this.fetchAdjustments();
        swal("Success","Adjustment deleted successfully.","success");
        this.isDeleteModalOpen = false;
        this.isLoading = false;
      } catch (error) {
        swal("Error","Failed to delete adjustment.","error");
        console.error(error);
      } finally {
        this.isDeleteModalOpen = false;
        this.isLoading = false;
      }
    },
    resetForm() {
      this.selectedAdjustmentType = "";
      this.amount = null;
      this.customAmount = null;
      this.reason = "";
      this.selectedModalMonth = this.selectedMonth || "";
      this.selectedModalYear = String(this.selectedYear) || "";
      this.selectedEmployeeId = this.selectedEmployee ? this.selectedEmployee.username : "";
      this.expenseAmount = null;
      this.receiptFile = null;
      this.receiptUrl = "";
      this.receiptError = "";
      this.typeError = this.amountError = this.monthError = this.yearError = this.employeeError = this.reasonError = "";
    },
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    checkReasonLength() {
      if (this.reason.length > 300) {
        this.reason = this.reason.slice(0, 300);
      }
    }
  }
};
</script>

<style scoped>
.optional-label {
  color: #9ca3af;
  font-weight: normal;
  font-size: 12px;
}
.receipt-current {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
  font-size: 13px;
}
.receipt-remove {
  background: none;
  border: none;
  padding: 0;
  color: #dc2626;
  cursor: pointer;
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// src/components/PayrollManagement/Adjustments/index.vue